import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { GroupBase } from 'react-select';
import { UserHasPermission } from 'utils/auth/roles-and-permissions';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { AddEditCustomOperation } from '@/modules/settings/property';
import { useGetJobsList } from '@/modules/workforce/Contracts/apiHooks';
import { useCreateEditContractUtils } from '@/modules/workforce/Contracts/components/CreateAndEdit/logic';
import { IContracts } from '@/modules/workforce/Contracts/types';
import { Checkbox, SelectField, Typography } from '@/shared/components/atoms';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { Item } from '@/shared/components/molecules/PartialListWithTooltip/IPartialListWithTooltip';
import { permissions } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const ContractJobs = () => {
  const { t } = useTranslation('common');
  const { watch, setValue } = useFormContext<IContracts.IAddContractSchema>();

  const { user } = useAuth();
  const { openModal } = useModal();

  const selectedEntityId = watch('entity');
  const supplier = watch('supplier');
  const startDateWatcher = watch('startDate');
  const watchedJobs = watch('jobs');

  const jobsIdsWatcher = useMemo(() => {
    return new Set(watchedJobs?.map((job) => job.value));
  }, [watchedJobs]);

  const { data, isFetching, refetch } = useGetJobsList({
    entityId: selectedEntityId?.id,
    supplierId: supplier?.id,
  });

  const { getCropLabelFormat, getJobLabelFormat, handleJobsListFormat } =
    useCreateEditContractUtils();

  const jobList = handleJobsListFormat(data, startDateWatcher);
  const jobOptions = jobList?.flatMap((item) => item.options);

  const allAvailableOptions = jobOptions.filter((job) => !job.isDisabled);

  const [isAllSelected, setIsAllSelected] = useState(
    jobsIdsWatcher.size === allAvailableOptions.length &&
      jobsIdsWatcher.size > 0
  );

  const handleSelectAll = () => {
    setIsAllSelected((prev) => {
      setValue('jobs', !prev ? allAvailableOptions : []);
      return !prev;
    });
  };

  useEffect(() => {
    if (jobsIdsWatcher.size < allAvailableOptions.length)
      setIsAllSelected(false);
  }, [allAvailableOptions, jobsIdsWatcher]);

  const canAddCustomOperation = UserHasPermission({
    action: permissions.CAN_ADD_CUSTOM_OPERATIONS,
    role: user?.userGroupId,
  });

  return (
    <SelectField<IContracts.IOption, true, GroupBase<IContracts.IOption>>
      id='jobs'
      isMulti
      isMultiGroupSelect
      isDisabled={!data && !isFetching}
      formatGroupLabel={(group: GroupBase<IContracts.IOption>) => (
        <div className='flex'>
          {getCropLabelFormat(data, group.label ?? '')}
        </div>
      )}
      menuPlacement='top'
      isSearchable
      isGroup
      borderColor={themeColors().grey[200]}
      width='w-full'
      py='0.5rem'
      px='0.5rem'
      borderRadius='1rem'
      label={t('Jobs')}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      placeholder={t('search-jobs')}
      formatOptionLabel={(option: IContracts.IOption, { context }) => {
        if (context === 'menu')
          return (
            <div className='flex w-full items-center justify-between gap-2'>
              <span className='flex items-center gap-9'>
                <Checkbox
                  className='mx-2  h-16 w-16'
                  onChange={() => null}
                  checked={jobsIdsWatcher.has(option.value)}
                  disabled={option.isDisabled}
                />
                <Typography variant='body2' className='text-primary-default'>
                  {option?.label}
                </Typography>
              </span>
              {option.isDisabled && (
                <span className='rounded-lg bg-neutralVariant-90 py-1 px-2 text-center text-12 text-surface-10'>
                  {t('contract-exists')}
                </span>
              )}
            </div>
          );
        if (context === 'value') {
          const job = getJobLabelFormat(data, option.value);
          return job ? (
            <PartialListWithTooltip
              contentClassName='py-2 text-8'
              imgProps={{
                size: 'xxs',
              }}
              items={[job]?.map((item: IContracts.IJobFormItemSchema) => {
                return {
                  description: (
                    <div className='flex w-full gap-2'>
                      {item.cropName} {item.job.taskName}
                    </div>
                  ),
                  imageURL: item.cropImage,
                } as Item;
              })}
              limit={jobsIdsWatcher?.size}
              hasBorder
            />
          ) : (
            option.label
          );
        }
      }}
      options={jobList}
      list={data}
      createOption={
        canAddCustomOperation
          ? {
              title: t('create-custom-operation'),
              subTitle: t('create-custom-operation-subtitle'),
              onClick: () => {
                openModal(
                  <AddEditCustomOperation
                    showCropSelection
                    cropId={0}
                    onSuccessFunction={() => {
                      refetch();
                      toast.success(t('custom-operation-added'));
                    }}
                  />,
                  {
                    title: t('create-custom-operation'),
                    variant: 'popup',
                    containerClassName: '!overflow-y-visible min-w-[362px]',
                  }
                );
              },
            }
          : undefined
      }
      getLabel={(item: IContracts.IOption) => item.label}
      // React-Select needs a primitive value from getOptionValue to set unique keys for multi values. Otherwise we run into duplicate items.
      getValue={(item: IContracts.IOption) => item.value}
      // Manually setting onChange so that the actual value in our form is the entire object
      onChange={(options) => setValue('jobs', options as IContracts.IOption[])}
      // manually set controlled value because it isn't primitive
      value={jobOptions?.filter((option) => jobsIdsWatcher?.has(option.value))}
      customMenuList={({ children }) => (
        <>
          <div className='mb-8 flex items-center gap-8 border-b-xs border-neutralVariant-90 px-12 py-16'>
            <Checkbox checked={isAllSelected} onChange={handleSelectAll} />
            <Typography>{t('select-all')}</Typography>
          </div>
          {children}
        </>
      )}
    />
  );
};

export default ContractJobs;
